import { HookFormTextarea } from '@finn/auto-ui/components/FormElements/HookFormTextarea';
import { SelectField } from '@finn/auto-ui/components/FormElements/SelectField';
import { Button } from '@finn/design-system/atoms/button';
import { If } from '@finn/design-system/atoms/If';
import { showToast } from '@finn/design-system/atoms/toast';
import { HookFormCheckbox, HookFormPhoneInput } from '@finn/platform-modules';
import { checkPhoneNumberValidation } from '@finn/ua-auth';
import { HookFormInput } from '@finn/ui-components';
import {
  cn,
  CountryCode,
  parseToHtml,
  trackEvent,
  useIntl,
} from '@finn/ui-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import { GDPRField } from './GDPRField';

type DynamicFormField = {
  type: {
    key:
      | 'text'
      | 'email'
      | 'number'
      | 'select'
      | 'checkbox'
      | 'gdpr'
      | 'textarea'
      | 'phone';
  };
  label?: string;
  name?: string;
  placeholder?: string;
  options?: Array<{ label: string; value: string }>;
  required?: boolean;
  defaultValue?: string;
  cols: '1' | '2';
};

type DynamicFormProps = {
  title?: string;
  description?: string;
  endpoint?: string;
  submit_label?: string;
  fields?: DynamicFormField[];
  extra_metadata?: {
    more_gdpr_info?: string;
    less_gdpr_info?: string;
    success_message?: string;
    failure_message?: string;
  };
  isInModal?: boolean;
  gdpr_details?: string;
  size?: 'small' | 'medium' | 'large';
};

export const DynamicForm = ({
  title,
  description,
  endpoint,
  fields,
  submit_label,
  extra_metadata,
  gdpr_details,
  isInModal,
  size = 'small',
}: DynamicFormProps) => {
  const { formatMessage } = useIntl();
  const schema = useMemo(() => {
    const rules = {};

    fields.forEach((field) => {
      if (field?.required) {
        if (field.type?.key === 'gdpr' || field.type?.key === 'checkbox') {
          rules[field?.name] = z.boolean().refine((value) => value === true, {
            message: formatMessage('yup.required'),
          });
        } else {
          rules[field?.name] = z.string({
            required_error: formatMessage('yup.required'),
          });
        }
      }
      if (field?.type?.key === 'phone' && rules[field?.name]) {
        rules[field?.name] = rules[field?.name]?.refine(
          (phonenumber) =>
            checkPhoneNumberValidation(phonenumber, CountryCode.DE),
          {
            message: formatMessage('yup.validPhoneNumber'),
          }
        );
      }
    });

    return z.object(rules);
  }, [fields, formatMessage]);

  const form = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: zodResolver(schema),
    defaultValues: fields.reduce((acc, field) => {
      if (field?.defaultValue !== undefined && field?.defaultValue !== null) {
        return {
          ...acc,
          [field?.name]: field?.defaultValue,
        };
      }

      return acc;
    }, {}),
  });

  const handleSubmit = useCallback(async () => {
    try {
      trackEvent('CTA Clicked', {
        location: 'dynamic-form',
        type: 'submit_dynamic_form',
        label: title,
        endpoint,
        ...form.getValues(),
      });
      await fetch('/api/cors-safe-hook-proxy', {
        method: 'POST',
        body: JSON.stringify({
          data: {
            ...form.getValues(),
            title,
            url: window?.location?.pathname,
          },
          endpoint,
        }),
      });
      showToast({
        status: 'success',
        description:
          extra_metadata?.success_message || 'Data sent successfully',
      });
    } catch (error) {
      showToast({
        status: 'error',
        description: extra_metadata?.failure_message || 'Failed to send data',
      });
    }
  }, [endpoint, title, form, extra_metadata]);

  return (
    <div className={size === 'large' ? 'flex gap-32' : ''}>
      <div className={size === 'large' ? 'w-1/2 min-w-[405px]' : ''}>
        <h2
          className={cn('mb-14 mobile-t3-semibold md:web-t3-semibold', {
            'mb-6': size === 'large',
          })}
        >
          {title}
        </h2>
        <div className="mb-6 body-16-light">{parseToHtml(description)}</div>
      </div>
      <FormProvider {...form}>
        <form
          className="grid grid-cols-2 gap-4 sm:gap-6"
          onSubmit={form.handleSubmit(handleSubmit, () => {
            trackEvent('CTA Clicked', {
              location: 'dynamic-form',
              type: 'submit_dynamic_form_failed',
              label: title,
              endpoint,
            });
          })}
        >
          {fields?.map((field) => {
            const { type, label, name, placeholder, options, cols } = field;
            const fieldType = type?.key;

            return (
              <div
                className={
                  cols === '1' && !isInModal
                    ? 'col-span-2 md:col-span-1'
                    : 'col-span-2'
                }
                key={field.name}
              >
                <If
                  condition={
                    fieldType === 'text' ||
                    fieldType === 'email' ||
                    fieldType === 'number'
                  }
                >
                  <HookFormInput
                    form={form}
                    name={name}
                    label={label}
                    placeholder={placeholder}
                    type={fieldType}
                  />
                </If>
                <If condition={fieldType === 'phone'}>
                  <HookFormPhoneInput
                    form={form}
                    name={name}
                    label={label}
                    placeholder={placeholder}
                  />
                </If>
                <If condition={fieldType === 'textarea'}>
                  <HookFormTextarea
                    form={form}
                    name={name}
                    placeholder={placeholder}
                  />
                </If>
                <If condition={fieldType === 'gdpr'}>
                  <GDPRField
                    form={form}
                    name={name}
                    label={label}
                    moreInfoTitle={extra_metadata?.more_gdpr_info}
                    lessInfoTitle={extra_metadata?.less_gdpr_info}
                    gdprDetails={gdpr_details}
                  />
                </If>
                <If condition={fieldType === 'checkbox'}>
                  <HookFormCheckbox form={form} name={name}>
                    {label}
                  </HookFormCheckbox>
                </If>
                <If condition={fieldType === 'select'}>
                  <SelectField
                    name={name}
                    label={label}
                    placeholder={placeholder}
                    options={options}
                  />
                </If>
              </div>
            );
          })}
          <Button
            type="submit"
            variant="primary"
            loading={form.formState.isSubmitting}
          >
            {submit_label}
          </Button>
        </form>
      </FormProvider>
    </div>
  );
};
